import { useState, type FormEvent } from 'react';
import { formatUsdt } from '@shared/types';
import { isValidTrc20Address } from '@shared/ledger';

import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { Dialog } from '@/components/ui/Dialog';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useTraderBalances } from '@/lib/queries';
import { useCreateWithdrawal } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { parseUsdtToMicro, usdt } from '@/lib/format';

/**
 * Requesting a withdrawal.
 *
 * The address is validated here, against the same helper the server uses, for a
 * reason that has nothing to do with saving a round trip: the trader doing this
 * is often standing outside on one bar of signal, and a rejected address after
 * fifteen seconds of spinner is how a withdrawal gets abandoned.
 *
 * The ceiling is enforced client-side too. Available is the only account a
 * trader can withdraw from, and the moment of refusal is the clearest place to
 * explain why the deposit is not part of that number.
 *
 * A typo in a TRC-20 address sends money nowhere recoverable, so the request is
 * two deliberate steps: fill in, then confirm against a restatement.
 */

interface Errors {
  amount?: string;
  address?: string;
}

export function WithdrawForm() {
  const toast = useToast();
  const balances = useTraderBalances();
  const createWithdrawal = useCreateWithdrawal();

  const [sourceAccount,setSourceAccount]=useState<'deposit'|'available'>('deposit');
  const [requestId,setRequestId]=useState(()=>crypto.randomUUID());
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const available = balances.data?.[sourceAccount] ?? null;
  const amountMicro = parseUsdtToMicro(amount);
  const trimmedAddress = address.trim();

  const validate = (): Errors => {
    const next: Errors = {};

    if (amount.trim() === '') {
      next.amount = 'Введите сумму.';
    } else if (Number.isNaN(amountMicro)) {
      next.amount = 'Сумма указана неверно. Введите число, например 25,00.';
    } else if (amountMicro <= 0) {
      next.amount = 'Сумма должна быть больше нуля.';
    } else if (available !== null && amountMicro > available) {
      next.amount =
        'Больше, чем доступно к выводу: ' +
        usdt(available) +
        '. Зарезервированные под платежи средства недоступны.';
    }

    if (trimmedAddress === '') {
      next.address = 'Введите адрес кошелька TRC-20.';
    } else if (!isValidTrc20Address(trimmedAddress)) {
      next.address =
        'Это не похоже на адрес TRC-20: он начинается с латинской T и состоит из 34 символов.';
    }

    return next;
  };

  const review = (event: FormEvent): void => {
    event.preventDefault();
    setServerError(null);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsConfirming(true);
  };

  const submit = async (): Promise<void> => {
    setServerError(null);
    try {
      await createWithdrawal.mutateAsync({ amountUsdt: amountMicro, address: trimmedAddress, sourceAccount, requestId });
      setIsConfirming(false);
      setRequestId(crypto.randomUUID());
      setAmount('');
      setAddress('');
      setErrors({});
      toast.success(
        'Заявка на вывод отправлена',
        'Она будет обработана автоматически. Статус виден в истории ниже.',
      );
    } catch (error) {
      setIsConfirming(false);
      setServerError(toMessage(error, 'Не удалось отправить заявку на вывод.'));
    }
  };

  const remaining =
    available === null ? null : Math.max(0, available - (Number.isNaN(amountMicro) ? 0 : amountMicro));

  return (
    <>
      <form onSubmit={review} noValidate className="max-w-prose">
        {serverError ? <InlineError className="mb-4" message={serverError} /> : null}

        <label className="mb-4 block">Источник средств
          <select value={sourceAccount} onChange={e=>{setSourceAccount(e.target.value as 'deposit'|'available');setRequestId(crypto.randomUUID());}} className="block mt-2 w-full bg-surface border border-line p-3">
            <option value="deposit">Свободный депозит</option><option value="available">Доступный баланс</option>
          </select>
        </label>
        <TextField
          label="Сумма"
          value={amount}
          onChange={(event) => {setAmount(event.target.value);setRequestId(crypto.randomUUID());}}
          error={errors.amount}
          hint={
            available === null
              ? 'Загружаю доступный баланс.'
              : 'Доступно к выводу: ' +
                usdt(available) +
                '. Сумма депозита резервируется при подаче заявки.'
          }
          /* A numeric keypad with a decimal separator. Not type=number: it
             brings spinners and a parser that disagrees with a Russian comma. */
          inputMode="decimal"
          autoComplete="off"
          monospace
          placeholder="0,00"
          aside={
            available !== null && available > 0 ? (
              <button
                type="button"
                onClick={() => setAmount(formatUsdt(available))}
                /* A real 44px box rather than `tap-target`: the expanded
                   overlay would reach over the top edge of the amount input
                   just below and steal taps meant for it. */
                className="focus-ring inline-flex min-h-11 items-center rounded-sm px-1 text-xs font-medium text-fg-accent underline underline-offset-2"
              >
                Вывести всё
              </button>
            ) : undefined
          }
        />

        <TextField
          containerClassName="mt-5"
          label="Адрес TRC-20"
          value={address}
          onChange={(event) => {setAddress(event.target.value);setRequestId(crypto.randomUUID());}}
          error={errors.address}
          hint="Сеть TRON (TRC-20). Адрес другой сети — потерянный перевод."
          /* A wallet address is case-sensitive base58: autocapitalisation and
             autocorrection on a phone keyboard quietly destroy it. */
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          monospace
          placeholder="T..."
        />

        <div className="mt-6">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={available === null}
            className="sm:w-auto"
          >
            Проверить и вывести
          </Button>
        </div>
      </form>

      <Dialog
        open={isConfirming}
        onClose={() => setIsConfirming(false)}
        busy={createWithdrawal.isPending}
        size="sm"
        title="Проверьте заявку"
        description="Перевод в блокчейне не отменяется. Сверьте адрес по символам: ошибка отправит средства туда, откуда их не вернуть."
        footer={
          <>
            <Button onClick={() => setIsConfirming(false)} disabled={createWithdrawal.isPending}>
              Назад
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              isLoading={createWithdrawal.isPending}
            >
              {createWithdrawal.isPending ? 'Отправляю...' : 'Отправить заявку'}
            </Button>
          </>
        }
      >
        <dl className="divide-y divide-line rounded-md border border-line">
          <div className="px-3 py-2.5"><dt>Источник</dt><dd>{sourceAccount==='deposit'?'Свободный депозит':'Доступный баланс'}</dd></div>
          <div className="px-3 py-2.5">
            <dt className="text-sm text-fg-muted">Сумма</dt>
            <dd className="num mt-1 text-md font-semibold text-fg">
              {Number.isNaN(amountMicro) ? '—' : usdt(amountMicro)}
            </dd>
          </div>
          <div className="px-3 py-2.5">
            <dt className="text-sm text-fg-muted">Адрес TRC-20</dt>
            {/* break-all rather than truncation: the trader is here to read
                every character, and an ellipsis hides where a typo hides. */}
            <dd className="num mt-1 break-all text-sm leading-normal text-fg">{trimmedAddress}</dd>
          </div>
          {remaining !== null ? (
            <div className="px-3 py-2.5">
              <dt className="text-sm text-fg-muted">Останется доступно</dt>
              <dd className="num mt-1 text-sm text-fg-secondary">{usdt(remaining)}</dd>
            </div>
          ) : null}
        </dl>
      </Dialog>
    </>
  );
}
